import { useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  fetchConfigurationsForLine,
  fetchConfigurationsForYear,
  fetchVehicleLines,
  fetchVehicleMakers,
} from 'lib/api/vehicle-catalog';
import {
  getYearsFromConfigurations,
  transformMakers,
  transformModelLines,
  transformModifications,
} from 'utils/vehicleCatalogUtils';

export function useCarMakers(onError) {
  const query = useQuery(['vehicle-makers'], fetchVehicleMakers, {
    staleTime: Infinity,
    onError: () => {
      if (onError) {
        onError('Failed to load car makers. Please try again.');
      }
    },
  });

  const carMakers = useMemo(
    () => (query.data ? transformMakers(query.data) : []),
    [query.data],
  );

  return {
    carMakers,
    loading: query.isLoading,
  };
}

export function useModelLines(carMakerId, onError) {
  const query = useQuery(
    ['vehicle-model-lines', carMakerId],
    () => fetchVehicleLines(carMakerId),
    {
      enabled: Boolean(carMakerId),
      staleTime: Infinity,
      onError: () => {
        if (onError) {
          onError('Failed to load model lines. Please try again.');
        }
      },
    },
  );

  const modelLines = useMemo(
    () =>
      query.data && carMakerId
        ? transformModelLines(query.data, carMakerId)
        : [],
    [query.data, carMakerId],
  );

  return {
    modelLines,
    loading: query.isLoading,
  };
}

export function useYears(modelLineId, carMakerId, onError) {
  const query = useQuery(
    ['vehicle-years', carMakerId, modelLineId],
    () => fetchConfigurationsForLine(carMakerId, modelLineId),
    {
      enabled: Boolean(carMakerId && modelLineId),
      staleTime: Infinity,
      onError: () => {
        if (onError) {
          onError('Failed to load years. Please try again.');
        }
      },
    },
  );

  const years = useMemo(
    () => (query.data ? getYearsFromConfigurations(query.data) : []),
    [query.data],
  );

  return {
    years,
    loading: query.isLoading,
  };
}

export function useModifications(modelLineId, year, carMakerId, onError) {
  const query = useQuery(
    ['vehicle-modifications', carMakerId, modelLineId, year],
    () => fetchConfigurationsForYear(carMakerId, modelLineId, year),
    {
      enabled: Boolean(carMakerId && modelLineId && year),
      staleTime: Infinity,
      onError: () => {
        if (onError) {
          onError('Failed to load modifications. Please try again.');
        }
      },
    },
  );

  const modifications = useMemo(
    () => (query.data ? transformModifications(query.data) : []),
    [query.data],
  );

  return {
    modifications,
    loading: query.isLoading,
  };
}
